import {
  formatProfileChoiceKey,
  isProfileChoiceKeyFormat,
  loadAIProfilesRegistry,
  matchStrictProfileInput,
  parseProfileChoiceKey,
  resolveSegmentKey,
} from "@x12i/ai-profiles";
import type { AIProfilesRegistry, RegistrySourceMode } from "@x12i/ai-profiles";
import { ProfileResolveKeyRequiredError } from "../errors.js";

function isBareProfileName(registry: AIProfilesRegistry, input: string): boolean {
  const key = input.trim().toLowerCase();
  if (!key || isProfileChoiceKeyFormat(key)) return false;
  return resolveSegmentKey(Object.keys(registry.profiles), key) !== undefined;
}

/**
 * Build an ai-profiles resolve key when the input is profile intent.
 *
 * Returns `null` for concrete model SKUs (e.g. `gemini-2.5-flash-lite`) so callers
 * can use catalog / registry index paths instead of {@link resolveAIProfile}.
 */
export function profileResolveKeyFromRegistry(
  registry: AIProfilesRegistry,
  input: string,
  explicitChoice?: string,
): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (explicitChoice) {
    const profileKey = isProfileChoiceKeyFormat(trimmed)
      ? parseProfileChoiceKey(trimmed)!.profile
      : trimmed;
    return formatProfileChoiceKey(profileKey, explicitChoice);
  }

  const strict = matchStrictProfileInput(registry, trimmed);
  if (strict?.kind === "profileChoice") {
    return formatProfileChoiceKey(strict.profile, strict.choice);
  }

  return null;
}

/** Require `profile/choice` or `profile` + explicit `choice`. */
export function requireProfileResolveKeyFromRegistry(
  registry: AIProfilesRegistry,
  input: string,
  explicitChoice?: string,
): string {
  const key = profileResolveKeyFromRegistry(registry, input, explicitChoice);
  if (key) return key;

  const trimmed = input.trim();
  if (trimmed && isBareProfileName(registry, trimmed)) {
    throw new ProfileResolveKeyRequiredError(trimmed);
  }

  throw new ProfileResolveKeyRequiredError(trimmed, {
    reason: "not_profile_intent",
  });
}

export async function requireProfileResolveKey(
  input: string,
  options: { source?: RegistrySourceMode; refresh?: boolean; choice?: string } = {},
): Promise<string> {
  const source = options.source ?? "auto";
  const registry = await loadAIProfilesRegistry({
    source,
    ...(source !== "auto" || options.refresh ? { refresh: true as const } : {}),
  });
  return requireProfileResolveKeyFromRegistry(registry, input, options.choice);
}

export async function profileResolveKey(
  input: string,
  options: { source?: RegistrySourceMode; refresh?: boolean; choice?: string } = {},
): Promise<string | null> {
  const source = options.source ?? "auto";
  const registry = await loadAIProfilesRegistry({
    source,
    ...(source !== "auto" || options.refresh ? { refresh: true as const } : {}),
  });
  return profileResolveKeyFromRegistry(registry, input, options.choice);
}
